import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { Intro } from "components/commons";
import { ConfirmModal, SuccessModal } from "components/modals";

import { generateReportAdmin, getFleetAccounts, updateFleetAccount } from "apis";
import { Path, AccountStatus, Role, Portal } from "enums";
import { useApi, useModal } from "hooks";
import useDataTable from "hooks/useDataTable";
import locale from "localization";
import { handleRequest } from "utils";

import { accountListColumns } from "./account-list-columns";
import AccountListFilter from "./account-list-filter";
import { accountListFilterState } from "./account-list-filter.state";
import { mapFilterToRequest, mapDataToList } from "./account-list.mapper";
import AccountList from "./account-list.list";
import useExport from "hooks/useExport";
import { SessionContext, UserContext } from "contexts";

const AccountListModule = (props) => {
  const history = useHistory();
  const confirmModal = useModal();
  const { sessionId } = useContext(SessionContext);
  const { close } = confirmModal;

  const { user } = useContext(UserContext);
  const { role } = user?.userAccesses?.find((element) => {
    return element.portal === Portal.LOCQ;
  });

  const { request: updateStatusRequest } = useApi({
    api: updateFleetAccount,
    handleOwnError: {
      badrequest: true,
    },
  });

  const handleUpdateStatus = (params, fleetName) => {
    const { status } = params;
    const activation = status === AccountStatus.Active;
    confirmModal.show({
      title: activation ? locale.reactivateFleetAccount : locale.deactivateFleetAccount,
      content: activation ? (
        <locale.Populate text={locale.reactivateFleetAccountContent} items={[<b>{fleetName}</b>]} />
      ) : (
        <locale.Populate text={locale.deactivateFleetAccountContent} items={[<b>{fleetName}</b>]} />
      ),
      secondary: {
        text: locale.Cancel,
      },
      primary: {
        text: activation ? locale.yesReactivateIt : locale.yesDeactivateIt,
        onClick: async () => {
          submitUpdateStatus(params);
          close();
        },
      },
    });
  };

  const { filter, search, table } = useDataTable({
    api: { api: getFleetAccounts },
    filter: { initialState: accountListFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "fleets",
      mapper: (fleets) =>
        mapDataToList({
          fleets,
          history,
          handleUpdateStatus,
          role,
        }),
      columns: accountListColumns,
      tableProps: { history, handleUpdateStatus },
    },
  });

  const submitUpdateStatus = async (params) => {
    handleRequest(async () => {
      const result = await updateStatusRequest({
        ...params,
      });
      if (result) table.fetch({});
    });
  };

  const exportReport = useExport({
    api: generateReportAdmin,
    hasModal: true,
    reportType: "fleet-account",
    mappedFilterState: {
      ...filter?.mappedFilterState,
      fleetStatus: filter?.mappedFilterState?.status,
      accountType: filter?.mappedFilterState?.accountType,
      searchKey: search?.searchKey,
      sessionId,
    },
  });

  return (
    <>
      <ConfirmModal {...confirmModal} />
      <SuccessModal {...exportReport?.successModalComponent} />
      {role === Role.Accounting ? (
        <div>
          <Intro title={locale.fleetAccountList} subtitle={locale.viewTrackFleetAccounts} />
        </div>
      ) : (
        <div>
          <Intro
            actionText={locale.addFleetAccount}
            actionOnClick={() => {
              history.push(Path.AddFleetAccount);
            }}
            title={locale.fleetAccountList}
            subtitle={locale.viewTrackFleetAccounts}
          />
        </div>
      )}

      <AccountListFilter {...filter} {...search} {...exportReport} />
      <AccountList table={table} />
    </>
  );
};

export default AccountListModule;
